import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography, keyframes } from '@mui/material';
import { BORDER_COLORS } from '../../utils/colors';
import { HERO_BUTTON } from '../../utils/responsive';
import { COUNTDOWN } from '../../constants/config';
import { useCountdown } from '../../hooks';
import ServerStatsRail from './ServerStatsRail';

// Keyframe animations - только opacity для героев, чтобы не конфликтовать с параллаксом
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(60px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const floatAnimation = keyframes`
  0%, 100% {
    transform: translateX(-50%) translateY(0);
  }
  50% {
    transform: translateX(-50%) translateY(-15px);
  }
`;

const glowPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(220, 38, 38, 0.15), 0 8px 30px rgba(0, 0, 0, 0.3);
  }
  50% {
    box-shadow: 0 0 40px rgba(220, 38, 38, 0.35), 0 8px 30px rgba(0, 0, 0, 0.3);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50%) scale(0.8);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) scale(1);
  }
`;

// Common hero styles
const heroBaseStyles = {
  position: 'absolute',
  objectFit: 'contain',
  transition: 'filter 0.2s ease-in, transform 0.1s ease',
  WebkitMaskImage: 'radial-gradient(ellipse 80% 90% at center 40%, black 50%, transparent 90%)',
  maskImage: 'radial-gradient(ellipse 80% 90% at center 40%, black 50%, transparent 90%)',
  '@media (min-width: 1025px)': {
    '&:hover': {
      filter: 'saturate(100%)',
    },
  },
};

/**
 * Отсчёт до запуска сервера под логотипом — ТОЛЬКО НА УЗКИХ ЭКРАНАХ (до `md`).
 *
 * На десктопе те же цифры показывает плавающая карточка `LaunchWidget` в правом нижнем углу,
 * и держать их ещё и в центре геро-блока незачем — центр освобождён под логотип и кнопку.
 * Но карточка живёт только от `md` (на телефоне она забирала пол-экрана и её там выключили),
 * поэтому ниже этой границы отсчёт остаётся здесь: иначе на мобильном срока старта не видно
 * нигде. Граница `md` — та же, по которой карточка появляется, чтобы между ними не было щели,
 * где отсчёт пропал у обоих или показан дважды.
 *
 * Прячем медиазапросом, а не `useMediaQuery`: страница уходит в пререндер, и разметка не должна
 * зависеть от ширины окна, в котором её собирали.
 *
 * Отдельный компонент, а не кусок разметки внутри `HeroSection`: `useCountdown` тикает раз в
 * секунду и перерисовывал вместе с собой весь геро-блок с пятью героями и параллаксом.
 */
const HeroCountdown: React.FC = () => {
  const { t } = useTranslation('home');
  const timeLeft = useCountdown();

  // `finished` — час X позади при выключенном счёте вверх: показывать нечего. Условие то же,
  // что у карточки (`LaunchWidget`), чтобы обе точки вывода гасли одновременно.
  if (!COUNTDOWN.ENABLED || timeLeft.finished) return null;

  return (
    <Box
      sx={{
        display: { xs: 'block', md: 'none' },
        textAlign: 'center',
        animation: `${fadeInUp} 1s ease-out 0.6s both`,
        mb: { xs: 2, sm: 2.5 },
      }}
    >
      <Typography
        sx={{
          color: 'rgba(255, 255, 255, 0.85)',
          fontSize: { xs: '0.6rem', sm: '0.7rem' },
          fontWeight: 600,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          textShadow: '0 0 10px rgba(0, 0, 0, 0.8)',
          mb: { xs: 0.5, sm: 1 },
        }}
      >
        {timeLeft.countingUp ? t('hero.countdown.uptimeTitle') : t('hero.countdown.title')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: { xs: 1, sm: 1.5 },
          justifyContent: 'center',
        }}
      >
        {[
          { value: timeLeft.days, label: t('hero.countdown.days') },
          { value: timeLeft.hours, label: t('hero.countdown.hours') },
          { value: timeLeft.minutes, label: t('hero.countdown.minutes') },
          { value: timeLeft.seconds, label: t('hero.countdown.seconds') },
        ].map((item, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minWidth: { xs: 40, sm: 50 },
            }}
          >
            <Typography
              sx={{
                color: '#ffffff',
                fontSize: { xs: '1.2rem', sm: '1.6rem' },
                fontWeight: 700,
                lineHeight: 1,
                fontVariantNumeric: 'tabular-nums',
                textShadow: '0 0 12px rgba(0, 0, 0, 0.9), 0 0 24px rgba(0, 0, 0, 0.6)',
              }}
            >
              {String(item.value).padStart(2, '0')}
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: { xs: '0.55rem', sm: '0.6rem' },
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                textShadow: '0 0 8px rgba(0, 0, 0, 1), 0 0 16px rgba(0, 0, 0, 0.8), 0 1px 3px rgba(0, 0, 0, 1)',
                mt: 0.3,
              }}
            >
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const HeroSection: React.FC = () => {
  const { t } = useTranslation('home');
  const [herosSaturated, setHerosSaturated] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const heroRefs = useRef<Map<string, HTMLImageElement>>(new Map());

  // Register hero element
  const registerHero = useCallback((id: string) => (el: HTMLImageElement | null) => {
    if (el) {
      heroRefs.current.set(id, el);
    }
  }, []);

  // Parallax effect с плавной интерполяцией
  useEffect(() => {
    if (window.innerWidth <= 1024) return;

    let targetX = 0;
    let targetY = 0;
    let currentX = 0;
    let currentY = 0;
    let animationId: number;

    const lerp = (start: number, end: number, factor: number) => {
      return start + (end - start) * factor;
    };

    const animate = () => {
      // Плавная интерполяция к целевой позиции
      currentX = lerp(currentX, targetX, 0.08);
      currentY = lerp(currentY, targetY, 0.08);

      const heroes = heroRefs.current;

      // Разные множители для разных слоёв - создаёт эффект глубины
      heroes.get('hero-1')?.style.setProperty('transform', `translate3d(${currentX * 0.8}px, ${currentY * 0.5}px, 0)`);
      heroes.get('hero-2')?.style.setProperty('transform', `translate3d(${currentX * 1.2}px, ${currentY * 0.7}px, 0)`);
      heroes.get('hero-3')?.style.setProperty('transform', `translate3d(${currentX * 1.5}px, ${currentY * 0.9}px, 0)`);
      heroes.get('hero-4')?.style.setProperty('transform', `translate3d(${-currentX * 0.8}px, ${currentY * 0.5}px, 0)`);
      heroes.get('hero-5')?.style.setProperty('transform', `translate3d(${-currentX * 1.2}px, ${currentY * 0.7}px, 0)`);

      animationId = requestAnimationFrame(animate);
    };

    const handleMouseMove = (e: MouseEvent) => {
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;
      const intensity = 25;

      targetX = (e.clientX - centerX) / intensity;
      targetY = (e.clientY - centerY) / intensity;
    };

    document.addEventListener('mousemove', handleMouseMove);
    animationId = requestAnimationFrame(animate);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationId);
    };
  }, []);

  const saturateFilter = herosSaturated ? 'saturate(100%)' : 'saturate(0%)';

  return (
    <Box sx={{ overflow: 'visible' }}>
      {/* Section Top */}
      <Box
        ref={containerRef}
        sx={{
          // `relative` — чтобы табличка с параметрами сервера считала свой правый край ОТ ОКНА.
          // Внутренний блок ниже ограничен `maxWidth: 1920` и на широких мониторах центрируется:
          // прибитая к нему табличка отъезжала от края экрана на сотни пикселей.
          position: 'relative',
          height: '57.552vw',
          mb: '75px',
          overflow: 'visible',
          '@media (min-width: 1921px)': {
            height: '1105px',
          },
          '@media (max-width: 1440px)': {
            mb: '170px',
          },
          // 118vw вместо 96.267: колонка с подзаголовком стала выше, и при прежней высоте
          // кнопка вылезала за секцию на 144px — упиралась в заголовок следующего блока.
          // Высота секции в vw, а содержимое колонки в пикселях, поэтому запас тут не «на глаз»:
          // при правках проверять УЗКИЙ конец диапазона, там он минимальный.
          //
          // 128vw вместо 118: 08.09.2026 подзаголовок разложили на две ступени (ведущая фраза
          // крупным кеглем), колонка выросла ещё на 34px, и на узком конце диапазона до
          // следующей секции оставалось 5px вместо прежних 44.
          //
          // ВНИМАНИЕ, ГРАБЛИ ЗАМЕРА: узкий конец диапазона — это НЕ `innerWidth: 391`.
          // Полосу выбирает CSS-ширина, которая меньше `innerWidth` на ширину полосы
          // прокрутки, поэтому окно 391px уходит в полосу `≤390`, а этот диапазон начинается
          // где-то с 398-402px по `innerWidth`. Мерить надо там.
          '@media (max-width: 750px)': {
            height: '128vw',
            mb: '72px',
          },
          '@media (max-width: 390px)': {
            height: '171.026vw',
            mb: '125px',
          },
        }}
      >
        {/* Параметры сервера — правый край ОКНА, по центру первого экрана (от lg).
            Висит на внешнем контейнере, а не на блоке ниже: тот шире 1920px не растёт. */}
        <ServerStatsRail />

        {/* Section BG */}
        <Box
          sx={{
            position: 'relative',
            top: '60px',
            maxWidth: '1920px',
            height: '56vw',
            width: '100%',
            mx: 'auto',
            overflow: 'visible',
            '@media (min-width: 1921px)': {
              height: '1075px',
            },
            '@media (max-width: 1440px)': {
              top: '50px',
            },
            '@media (max-width: 750px)': {
              top: '40px',
              height: '110vw',
            },
            // 24px, а не прежние 70: на узких экранах между шапкой и логотипом висело 94px
            // пустоты, а кнопка при этом жалась к нижнему краю. `top` здесь двигает ВЕСЬ первый
            // экран разом — фон, героев, логотип и колонку с текстом, — поэтому композиция
            // внутри не меняется, блок просто встаёт выше.
            '@media (max-width: 390px)': {
              top: '24px',
              height: '180vw',
            },
          }}
        >
          {/* Background */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '100vw',
              height: '100%',
              zIndex: 1,
              backgroundImage: 'url(/img/home/hero-section-background.webp)',
              backgroundSize: 'cover',
              backgroundPosition: 'center center',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                right: 0,
                top: 0,
                height: '200px',
                background: 'linear-gradient(to bottom, #0a0a0a 0%, transparent 100%)',
                zIndex: 1,
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                height: '50%',
                background: 'linear-gradient(to top, #0a0a0a 0%, transparent 100%)',
              },
            }}
          />

          {/* Hero 1 */}
          <Box
            component="img"
            ref={registerHero('hero-1')}
            src="/img/home/intro-hero-1.webp"
            alt="Hero"
            sx={{
              ...heroBaseStyles,
              filter: saturateFilter,
              animation: `${fadeIn} 1.2s ease-out 0.3s both`,
              top: '-6.146vw',
              left: '7.24vw',
              zIndex: 2,
              width: '34.115vw',
              '@media (min-width: 1921px)': {
                top: '-118px',
                left: '139px',
                width: '655px',
              },
              '@media (max-width: 1440px)': {
                top: 0,
                left: '0.889vw',
                width: '39.722vw',
              },
              '@media (max-width: 750px)': {
                top: '-14.667vw',
                left: '-18.133vw',
                width: '61.733vw',
              },
              '@media (max-width: 390px)': {
                top: '-27.949vw',
                left: '-40.256vw',
                width: '109.744vw',
              },
            }}
          />

          {/* Hero 2 */}
          <Box
            component="img"
            ref={registerHero('hero-2')}
            src="/img/home/intro-hero-2.webp"
            alt="Hero"
            sx={{
              ...heroBaseStyles,
              filter: saturateFilter,
              animation: `${fadeIn} 1.2s ease-out 0.5s both`,
              top: '15.833vw',
              left: 0,
              zIndex: 4,
              width: '23.125vw',
              '@media (min-width: 1921px)': {
                top: '304px',
                width: '444px',
              },
              '@media (max-width: 1440px)': {
                top: '18.056vw',
                left: '-10.944vw',
                width: '32.222vw',
              },
              '@media (max-width: 750px)': {
                top: '28vw',
                left: '-25.067vw',
                width: '50.133vw',
                zIndex: 12,
              },
              '@media (max-width: 390px)': {
                top: '80.974vw',
                left: '-45.974vw',
                width: '70.974vw',
              },
            }}
          />

          {/* Hero 3 */}
          <Box
            component="img"
            ref={registerHero('hero-3')}
            src="/img/home/intro-hero-3.webp"
            alt="Hero"
            sx={{
              ...heroBaseStyles,
              filter: saturateFilter,
              animation: `${fadeIn} 1.2s ease-out 0.7s both`,
              top: '28.229vw',
              left: '9.01vw',
              zIndex: 3,
              width: '35.99vw',
              '@media (min-width: 1921px)': {
                top: '543px',
                left: '173px',
                width: '691px',
              },
              '@media (max-width: 1440px)': {
                top: '34.167vw',
                left: '-2.361vw',
                width: '43.611vw',
              },
              '@media (max-width: 750px)': {
                top: '53.067vw',
                left: '-17.867vw',
                width: '67.733vw',
                zIndex: 13,
              },
              '@media (max-width: 390px)': {
                top: '100.103vw',
                left: '-43.077vw',
                width: '120.513vw',
              },
            }}
          />

          {/* Hero 4 */}
          <Box
            component="img"
            ref={registerHero('hero-4')}
            src="/img/home/intro-hero-4.webp"
            alt="Hero"
            sx={{
              ...heroBaseStyles,
              filter: saturateFilter,
              animation: `${fadeIn} 1.2s ease-out 0.3s both`,
              top: '-7.76vw',
              right: 0,
              zIndex: 2,
              width: '41.042vw',
              '@media (min-width: 1921px)': {
                top: '-149px',
                width: '788px',
              },
              '@media (max-width: 1440px)': {
                top: 0,
                right: '-3.472vw',
                width: '46.736vw',
              },
              '@media (max-width: 750px)': {
                top: '-13.333vw',
                right: '-25.333vw',
                width: '72.667vw',
              },
              '@media (max-width: 390px)': {
                top: '-43.846vw',
                right: '-64.359vw',
                zIndex: 9,
                width: '128.974vw',
              },
            }}
          />

          {/* Hero 5 */}
          <Box
            component="img"
            ref={registerHero('hero-5')}
            src="/img/home/intro-hero-5.webp"
            alt="Hero"
            sx={{
              ...heroBaseStyles,
              filter: saturateFilter,
              animation: `${fadeIn} 1.2s ease-out 0.5s both`,
              top: '27.917vw',
              right: '2.396vw',
              zIndex: 3,
              width: '38.698vw',
              '@media (min-width: 1921px)': {
                top: '536px',
                right: '46px',
                width: '743px',
              },
              '@media (max-width: 1440px)': {
                top: '33.944vw',
                right: '-6.875vw',
                width: '51.806vw',
              },
              '@media (max-width: 750px)': {
                top: '41.867vw',
                right: '-22.4vw',
                width: '80.533vw',
              },
              '@media (max-width: 390px)': {
                top: '88.359vw',
                right: '-53.077vw',
                width: '143.077vw',
              },
            }}
          />

          {/* Logo */}
          <Box
            component="img"
            src="/img/home/logo.svg"
            alt="Odium World Logo"
            sx={{
              position: 'absolute',
              top: '10.292vw',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1,
              width: '24vw',
              objectFit: 'contain',
              animation: `${scaleIn} 1s ease-out 0.2s both, ${floatAnimation} 4s ease-in-out 1.5s infinite`,
              '@media (min-width: 1921px)': {
                top: '140px',
                width: '695px',
              },
              '@media (max-width: 1440px)': {
                width: '41.181vw',
              },
              // Поднят с 22vw: между шапкой и логотипом висело больше 100px пустоты, а блок
              // целиком сидел слишком низко. Меняешь — пересчитай `top` колонки ниже.
              '@media (max-width: 750px)': {
                top: '15vw',
                width: '64vw',
                zIndex: 10,
              },
              // На узких экранах логотип был 113.59vw (на 320px это 363px — ШИРЕ самого экрана)
              // и висел на 28vw сверху. Из-за этого на iPhone SE/5 кнопка уходила на 60px ниже
              // сгиба: до неё нельзя было добраться, не прокрутив первый экран. Ужат до 96vw
              // и поднят. Меняешь эти два числа — пересчитай `top` колонки ниже: он считается
              // от низа РИСУНКА логотипа, а тот зависит и от ширины, и от отступа сверху.
              '@media (max-width: 390px)': {
                top: '12vw',
                width: '96vw',
              },
            }}
          />

          {/* Center Items - подзаголовок + Countdown (мобильный) + Button.

              Колонка растянута ОТ КРАЯ ДО КРАЯ (`left: 0, right: 0`), а не привязана к середине
              через `left: 50%` + `translateX(-50%)`. Разница видна только с широким содержимым:
              у абсолютного блока без заданной ширины она считается shrink-to-fit, и при `left: 50%`
              потолок — ровно половина экрана. Кнопке и отсчёту этого хватало, а подзаголовок на
              телефоне сжимался до 195px и разваливался на семь строк. Дети центрируются
              `alignItems: center`, ширину текста держит его собственный `maxWidth`. */}
          <Box
            sx={{
              position: 'absolute',
              // `top` считается ОТ ВИДИМОГО НИЗА ЛОГОТИПА, а не от круглого числа: у logo.svg
              // снизу ~18% прозрачного поля (при 424px высоты бокса рисунок кончается на 349-й
              // строке), и подзаголовок, поставленный по нижней грани бокса, визуально липнет
              // к кнопке. Числа перед `vw` — низ рисунка в vw для своего диапазона, замерено
              // сканом альфа-канала; слагаемое в px — половина свободного зазора. Такое же
              // слагаемое стоит у подзаголовка в `mb`: сверху и снизу от него получается
              // поровну. Меняешь одно — пересчитай второе, иначе строка снова уползёт к кнопке.
              top: 'calc(27.8vw + 12px)',
              left: 0,
              right: 0,
              zIndex: 90,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              '@media (min-width: 1921px)': {
                top: '586px',
              },
              '@media (max-width: 1440px)': {
                top: 'calc(36.25vw + 12px)',
              },
              '@media (max-width: 750px)': {
                top: 'calc(55.8vw + 10px)',
              },
              '@media (max-width: 390px)': {
                top: 'calc(73.63vw + 8px)',
              },
            }}
          >
            {/* Подзаголовок — одна фраза о сервере между логотипом и кнопкой. Ширина ОГРАНИЧЕНА,
                и это не вкусовщина: без неё на широком мониторе фраза растягивается в одну строку
                через полэкрана и перестаёт читаться как подпись под логотипом. Формулировка —
                короткая версия той, что на лендинге; расходиться двум витринам одного сервера
                нельзя (`docs/landing.md`). */}
            <Typography
              sx={{
                maxWidth: { xs: 290, sm: 440, md: 540 },
                px: 2,
                textAlign: 'center',
                // Шрифт — Spectral, единственное место на сайте, где не Inter. Строка под
                // логотипом читается как фраза, а не как подпись интерфейса, и серифная
                // антиква даёт это, ничего не крича. Playfair тут пробовали раньше и отказались:
                // прямой читался тяжело, курсив вычурно. Spectral выбран из пяти кандидатов,
                // показанных заказчику прямо на арте (Spectral / PT Serif / Cormorant Garamond /
                // Manrope / Inter) — у него самый крепкий контур на пёстром фоне среди серифов.
                //
                // Кегль на ступень крупнее, чем был у Inter: у Spectral мельче строчные, и на
                // прежних размерах строка проседала рядом с логотипом.
                fontFamily: '"Spectral", Georgia, serif',
                fontWeight: 400,
                color: 'rgba(255, 255, 255, 0.86)',
                fontSize: { xs: '0.92rem', sm: '1.02rem', md: '1.12rem' },
                letterSpacing: '0.01em',
                lineHeight: 1.7,
                // Строка лежит поверх арта, а он местами светлый — плащи, дым, свечение логотипа.
                // Читаемость держат ВЕС и ТЕНЬ, без подложки под текстом: подложку пробовали,
                // заказчику не зашла. Вес 400, а не Light 300 — тонкие штрихи на пёстром фоне
                // разваливались первыми. Тень в три уровня: узкая держит контур, широкие гасят
                // светлые пятна под строкой.
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.95), 0 0 12px rgba(0, 0, 0, 0.9), 0 0 28px rgba(0, 0, 0, 0.75)',
                animation: `${fadeInUp} 1s ease-out 0.5s both`,
                // Зеркало зазора сверху (см. `top` колонки). Границы медиазапросов те же, что
                // у колонки, а НЕ брейкпоинты MUI: разъедутся — центровка поедет вместе с ними.
                mb: 'calc(3.45vw + 12px)',
                '@media (min-width: 1921px)': { mb: '38px' },
                '@media (max-width: 1440px)': { mb: 'calc(1.75vw + 12px)' },
                '@media (max-width: 750px)': { mb: 'calc(3.2vw + 10px)' },
                '@media (max-width: 390px)': { mb: 'calc(5.21vw + 8px)' },
              }}
            >
              {/* Две ступени: первая фраза — заголовок, остальное — пояснение под ним. Одной
                  ровной строкой блок читался как подпись и терялся между логотипом и кнопкой
                  («как-то можно его больше сделать, более выразнее»). Кегль ведущей фразы на
                  узких экранах поднят СДЕРЖАННО: геро-блок на телефоне уже впритык, на 391px
                  до следующей секции остаётся меньше полусотни пикселей (см. замеры ниже по
                  файлу и в docs/ui-patterns.md). */}
              <Box
                component="span"
                sx={{
                  display: 'block',
                  color: '#fff',
                  fontSize: { xs: '1.12rem', sm: '1.4rem', md: '1.75rem' },
                  // Интерлиньяж и отступ поджаты ровно ради телефонов: на 1440 ведущая фраза
                  // укладывается в одну строку и их не замечает, а на 391px она идёт в две,
                  // и каждый лишний пиксель тут вычитается из зазора до следующей секции.
                  lineHeight: 1.25,
                  mb: '0.25em',
                }}
              >
                {t('hero.taglineLead')}
              </Box>
              {t('hero.taglineRest')}
            </Typography>
            {/* Отсчёт до запуска сервера — только до `md`; выше его показывает карточка LaunchWidget */}
            <HeroCountdown />

            <Button
              component={Link}
              to="/login"
              onMouseEnter={() => setHerosSaturated(true)}
              onMouseLeave={() => setHerosSaturated(false)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: HERO_BUTTON.height,
                background: 'rgba(0, 0, 0, 0.4)',
                backdropFilter: 'blur(16px)',
                border: `1px solid ${BORDER_COLORS.DEFAULT}`,
                color: '#fff',
                fontSize: HERO_BUTTON.fontSize,
                fontWeight: 500,
                letterSpacing: HERO_BUTTON.letterSpacing,
                px: HERO_BUTTON.px,
                borderRadius: HERO_BUTTON.borderRadius,
                textDecoration: 'none',
                textTransform: 'uppercase',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                animation: `${fadeInUp} 1s ease-out 0.8s both, ${glowPulse} 2.5s ease-in-out 2s infinite`,
                position: 'relative',
                overflow: 'hidden',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: '20%',
                  right: '20%',
                  height: '2px',
                  background: '#dc2626',
                  borderRadius: '2px',
                  boxShadow: '0 0 15px rgba(220, 38, 38, 0.6)',
                  transition: 'all 0.4s ease',
                },
                '&:hover': {
                  transform: 'translateY(-4px)',
                  background: 'rgba(220, 38, 38, 0.1)',
                  borderColor: 'rgba(220, 38, 38, 0.3)',
                  boxShadow: '0 20px 50px rgba(0, 0, 0, 0.4), 0 0 30px rgba(220, 38, 38, 0.15)',
                  animationPlayState: 'paused',
                  '&::after': {
                    left: '5%',
                    right: '5%',
                    boxShadow: '0 0 25px rgba(220, 38, 38, 0.8)',
                  },
                },
                '&:active': {
                  transform: 'translateY(-2px)',
                },
              }}
            >
              {t('hero.startGame')}
            </Button>
          </Box>

        </Box>
      </Box>
    </Box>
  );
};

export default HeroSection;
